// services/live/roomChannel — the private Broadcast topic of ONE group room,
// `room:<roomId>:<liveKey>` (LANE1_PLAN.md §2, §5; migration 0079), for the
// room that is OPEN on screen. Live v2, slice 2 (FEATURES.liveV2Rooms).
//
// What travels on it is published by database triggers, never by a client:
//   message_created / message_updated  {op, room_id, message_id, at, row}
//                                       (or thin=true and no row: fetch it)
//   message_deleted                     {op, room_id, message_id, at}
//   reaction_changed                    {message_id, room_id, user_id, emoji, op}
//   pins_changed / links_changed        {room_id, at}
//
// Rules this module keeps (the same ones userChannel / scopedChannel keep):
//   • the topic name needs the room's CURRENT key, read once from rooms.live_key
//     through the normal API (a member may read their room row). A database
//     without 0079 answers "no such column" → `onUnavailable`, and the consumer
//     opens its old postgres_changes binding instead — silent fallback;
//   • one channel per room per tab, ref-counted: RoomChat, the pins hook and
//     the media hook all attach to the same join;
//   • private: true; the join is authorised ONCE by the realtime.messages
//     policy (member of the room AND key = rooms.live_key), never per message;
//   • a broadcast is a hint: every (re)join asks the consumer to run its cursor
//     catch-up (`onResync`) — Broadcast is at-most-once, the catch-up is the
//     guarantee;
//   • KEY ROTATION: when someone is removed from the room its key rotates in
//     the same transaction (0079). The remaining members receive
//     `room_key_changed {room_id, live_key}` on their user:<id> topic; this
//     module leaves the dead topic, joins the new one and asks for a catch-up.
//     The removed person never receives the new key and is refused it anyway.
//     If the hint was missed (tab offline during the removal), the old topic's
//     join is refused on reconnect; a CHANNEL_ERROR after having been live
//     re-reads the key (throttled) and rejoins when it changed.
import { supabase } from '../supabaseClient';
import { isMissingSchema } from '../roomActionsParity';
import { joinScopedChannel, type ScopedListener } from './scopedChannel';
import { joinUserChannel, onUserEvent } from './userChannel';

export const ROOM_EVENTS = [
  'message_created', 'message_updated', 'message_deleted',
  'reaction_changed', 'pins_changed', 'links_changed',
] as const;
export type RoomEvent = typeof ROOM_EVENTS[number];

export const KEY_CHANGED_EVENT = 'room_key_changed';
/** A CHANNEL_ERROR after having been live re-reads the key this long later. */
export const KEY_RECHECK_DELAY_MS = 5000;
/** …and never more often than this. */
export const KEY_RECHECK_MIN_GAP_MS = 30000;
/** A database that answered "no live_key column" is not asked again for a while. */
const SCHEMA_MISSING_TTL_MS = 10 * 60 * 1000;

export interface RoomChannelListener {
  onEvent: (event: string, payload: any) => void;
  /** Re-joined after a drop, or moved to a rotated key: run the cursor catch-up. */
  onResync: () => void;
  /** No live path for this room (0079 missing, join refused): open the old binding. */
  onUnavailable: () => void;
  /** The first join of the current topic succeeded. */
  onLive?: () => void;
  /** Raw channel statuses, for the consumer's liveness clock. */
  onStatus?: (status: string) => void;
}

interface RoomState {
  roomId: string;
  key: string | null;
  topic: string | null;
  listeners: Set<RoomChannelListener>;
  leaveScoped: (() => void) | null;
  /** The current topic has been SUBSCRIBED at least once. */
  live: boolean;
  dead: boolean;
  /** This join replaces a rotated key: its first SUBSCRIBED is a resync. */
  rotated: boolean;
  recheckTimer: ReturnType<typeof setTimeout> | null;
  lastRecheckAt: number;
  rechecking: boolean;
}

export const roomTopic = (roomId: string, key: string): string => `room:${roomId}:${key}`;

const rooms = new Map<string, RoomState>();
let schemaMissingUntil = 0;
// The user:<id> channel carries room_key_changed; hold it while any room is joined.
let userRelease: (() => void) | null = null;
let userHeld: string | null = null;
let userReleaseTimer: ReturnType<typeof setTimeout> | null = null;
let keyEventOff: (() => void) | null = null;
// The last room leaving releases the user channel a moment LATER: a room
// switch (leave, then join) in one breath must not cost a user:<id> rejoin.
let userReleaseGraceMs = 1500;
/** Test seam. */
export function _setUserReleaseGraceMs(ms: number): void { userReleaseGraceMs = ms; }

function releaseUserChannel(): void {
  if (userReleaseTimer) { clearTimeout(userReleaseTimer); userReleaseTimer = null; }
  const rel = userRelease;
  userRelease = null;
  userHeld = null;
  if (rel) { try { rel(); } catch { /* ignore */ } }
}

function each(st: RoomState, fn: (l: RoomChannelListener) => void): void {
  for (const l of [...st.listeners]) { try { fn(l); } catch { /* one bad consumer must not starve the rest */ } }
}

function teardown(st: RoomState): void {
  st.dead = true;
  if (st.recheckTimer) { clearTimeout(st.recheckTimer); st.recheckTimer = null; }
  rooms.delete(st.roomId);
  const leave = st.leaveScoped;
  st.leaveScoped = null;
  if (leave) { try { leave(); } catch { /* ignore */ } }
  if (rooms.size === 0 && userRelease && !userReleaseTimer) {
    if (userReleaseGraceMs <= 0) { releaseUserChannel(); return; }
    userReleaseTimer = setTimeout(() => {
      userReleaseTimer = null;
      if (rooms.size === 0) releaseUserChannel();
    }, userReleaseGraceMs);
  }
}

function fail(st: RoomState): void {
  if (st.dead) return;
  const ls = [...st.listeners];
  st.listeners.clear();
  teardown(st);
  ls.forEach((l) => { try { l.onUnavailable(); } catch { /* ignore */ } });
}

type KeyRead = { key: string } | { missing: true } | { error: true };

async function readRoomKey(roomId: string): Promise<KeyRead> {
  try {
    const { data, error } = await supabase()
      .from('rooms')
      .select('live_key')
      .eq('id', roomId)
      .maybeSingle();
    if (error) return isMissingSchema(error) ? { missing: true } : { error: true };
    const key = (data as any)?.live_key;
    if (typeof key !== 'string' || !key) return { error: true };
    return { key };
  } catch {
    return { error: true };
  }
}

function joinTopic(st: RoomState, key: string): void {
  st.key = key;
  st.topic = roomTopic(st.roomId, key);
  st.live = false;
  const listener: ScopedListener = {
    onEvent: (event, payload) => { if (!st.dead) each(st, (l) => l.onEvent(event, payload)); },
    onResync: () => { if (!st.dead) each(st, (l) => l.onResync()); },
    onUnavailable: () => { if (!st.dead) fail(st); },
    onLive: () => {
      if (st.dead) return;
      st.live = true;
      const rotated = st.rotated;
      st.rotated = false;
      each(st, (l) => l.onLive?.());
      // Messages sent between leaving the dead topic and joining this one were
      // never delivered to us — say so.
      if (rotated) each(st, (l) => l.onResync());
    },
    onStatus: (status) => {
      if (st.dead) return;
      each(st, (l) => l.onStatus?.(status));
      if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && st.live) scheduleKeyRecheck(st);
    },
  };
  st.leaveScoped = joinScopedChannel(st.topic, [...ROOM_EVENTS], listener);
}

function rejoinWithKey(st: RoomState, key: string): void {
  if (st.dead || key === st.key) return;
  const leave = st.leaveScoped;
  st.leaveScoped = null;
  if (leave) { try { leave(); } catch { /* ignore */ } }
  st.rotated = true;
  joinTopic(st, key);
}

function scheduleKeyRecheck(st: RoomState): void {
  if (st.recheckTimer || st.rechecking) return;
  const gap = Date.now() - st.lastRecheckAt;
  const wait = Math.max(KEY_RECHECK_DELAY_MS, KEY_RECHECK_MIN_GAP_MS - gap);
  st.recheckTimer = setTimeout(() => {
    st.recheckTimer = null;
    if (st.dead) return;
    st.rechecking = true;
    st.lastRecheckAt = Date.now();
    void readRoomKey(st.roomId).then((r) => {
      st.rechecking = false;
      if (st.dead) return;
      if ('key' in r && r.key !== st.key) rejoinWithKey(st, r.key);
    });
  }, wait);
}

async function open(st: RoomState): Promise<void> {
  const r = await readRoomKey(st.roomId);
  if (st.dead) return;
  if ('missing' in r) {
    schemaMissingUntil = Date.now() + SCHEMA_MISSING_TTL_MS;
    fail(st);
    return;
  }
  if ('error' in r) { fail(st); return; }
  joinTopic(st, r.key);
}

function onKeyChanged(payload: any): void {
  const roomId = String(payload?.room_id ?? '');
  const key = payload?.live_key;
  if (!roomId || typeof key !== 'string' || !key) return;
  const st = rooms.get(roomId);
  if (!st || st.dead) return;
  rejoinWithKey(st, key);
}

function holdUserChannel(userId: string | undefined): void {
  if (!keyEventOff) keyEventOff = onUserEvent(KEY_CHANGED_EVENT, onKeyChanged);
  if (!userId) return;
  if (userReleaseTimer) { clearTimeout(userReleaseTimer); userReleaseTimer = null; }
  if (userHeld === userId && userRelease) return;
  releaseUserChannel();
  userHeld = userId;
  userRelease = joinUserChannel(userId);
}

/**
 * Join the open room's topic. Returns leave(). `userId` (when known) keeps the
 * user:<id> channel open so a key rotation reaches this tab.
 */
export function joinRoomChannel(roomId: string, listener: RoomChannelListener, userId?: string): () => void {
  if (schemaMissingUntil > Date.now()) {
    let cancelled = false;
    // Async like a real refusal, so callers have one code path.
    void Promise.resolve().then(() => { if (!cancelled) listener.onUnavailable(); });
    return () => { cancelled = true; };
  }
  holdUserChannel(userId);
  let st = rooms.get(roomId);
  if (!st) {
    st = {
      roomId, key: null, topic: null, listeners: new Set(), leaveScoped: null,
      live: false, dead: false, rotated: false, recheckTimer: null, lastRecheckAt: 0, rechecking: false,
    };
    rooms.set(roomId, st);
    st.listeners.add(listener);
    void open(st);
  } else {
    st.listeners.add(listener);
    if (st.live) {
      const l = listener;
      void Promise.resolve().then(() => { if (st!.listeners.has(l)) l.onLive?.(); });
    }
  }
  const mine = st;
  return () => {
    if (!mine.listeners.delete(listener)) return;
    if (mine.listeners.size > 0 || mine.dead) return;
    teardown(mine);
  };
}

/** The current topic of a joined room (tests, diagnostics). */
export function currentRoomTopic(roomId: string): string | null {
  return rooms.get(roomId)?.topic ?? null;
}

// ── one-shot schema probe for consumers that have no room to key on ────────
let schemaProbe: Promise<boolean> | null = null;
/**
 * Does this database carry 0079 (rooms.live_key)? One read per tab session.
 * A transient failure answers true — the channel join then decides.
 */
export function probeRoomLiveSchema(): Promise<boolean> {
  if (schemaProbe) return schemaProbe;
  schemaProbe = (async () => {
    try {
      const { error } = await supabase().from('rooms').select('live_key').limit(1);
      if (error) {
        if (isMissingSchema(error)) { schemaMissingUntil = Date.now() + SCHEMA_MISSING_TTL_MS; return false; }
        schemaProbe = null;
        return true;
      }
      return true;
    } catch {
      schemaProbe = null;
      return true;
    }
  })();
  return schemaProbe;
}

// ── pure helpers for the consumers ─────────────────────────────────────────
export interface MessageChange {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: any;
  old: any;
  table: 'room_messages';
}

/**
 * A room event → the shape RoomChat's postgres_changes handler already takes.
 * `null` for events that carry no row (a thin hint: fetch by id; anything else).
 */
export function messageEventToChange(event: string, payload: any): MessageChange | null {
  if (!payload || typeof payload !== 'object') return null;
  if (event === 'message_deleted') {
    const id = payload.message_id;
    if (id == null) return null;
    return { eventType: 'DELETE', new: {}, old: { id: String(id) }, table: 'room_messages' };
  }
  if (event !== 'message_created' && event !== 'message_updated') return null;
  const row = payload.row;
  if (payload.thin || !row || typeof row !== 'object' || row.id == null) return null;
  return { eventType: event === 'message_created' ? 'INSERT' : 'UPDATE', new: row, old: {}, table: 'room_messages' };
}

/** Resolve a thin hint: the row by id, or null. Never throws. */
export async function fetchRoomMessageRow(messageId: string): Promise<any | null> {
  try {
    const { data, error } = await supabase()
      .from('room_messages')
      .select('*')
      .eq('id', messageId)
      .maybeSingle();
    if (error || !data) return null;
    return data;
  } catch {
    return null;
  }
}

export type ReactionJson = Record<string, string[]> | null | undefined;

/**
 * Apply one reaction_changed event to a message's `reactions` jsonb
 * ({emoji: [uid, …]}): INSERT/UPDATE = this user now reacts with `emoji` and
 * nothing else; DELETE = they no longer react. Returns the SAME object when
 * nothing changed. The mirrored row that follows (message_updated) wins.
 */
export function applyReactionChange(
  reactions: ReactionJson,
  userId: string,
  emoji: string,
  op: string,
): ReactionJson {
  if (!userId) return reactions;
  const cur = reactions ?? {};
  const adding = op !== 'DELETE' && !!emoji;
  const has = (e: string) => Array.isArray(cur[e]) && cur[e].includes(userId);
  const elsewhere = Object.keys(cur).some((e) => e !== emoji && has(e));
  // Already exactly this state: same object, so nothing re-renders.
  if (adding && has(emoji) && !elsewhere) return reactions;
  if (!adding && !has(emoji) && !elsewhere) return reactions;
  const next: Record<string, string[]> = {};
  for (const [e, uids] of Object.entries(cur)) {
    const kept = (Array.isArray(uids) ? uids : []).filter((u) => u !== userId);
    if (kept.length) next[e] = kept;
  }
  if (adding) next[emoji] = [...(next[emoji] ?? []), userId];
  return Object.keys(next).length ? next : null;
}

/** Test seam: forget every room and the schema verdict. */
export function _resetRoomChannels(): void {
  [...rooms.values()].forEach(teardown);
  rooms.clear();
  schemaMissingUntil = 0;
  schemaProbe = null;
  if (keyEventOff) { keyEventOff(); keyEventOff = null; }
  releaseUserChannel();
}
